import { backButton, miniApp } from '@tma.js/sdk-react'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router'
import { isTelegram } from 'shared/lib/telegram/platform'

/**
 * Set to `true` to keep the Back Button visible at the root page and close the
 * Mini App when pressed.  `false` = hide at the root page.
 */
const CLOSE_AT_ROOT = false

/**
 * Centralised Telegram Back Button controller.
 *
 * Mount once inside the router (e.g. in `AppLayout`).
 * Tracks an app-local navigation stack so `navigate(-1)` is only called
 * when there is a known internal route to go back to.
 *
 * Replaces per-page `backButton.show()` / `backButton.hide()` calls.
 */
export function TelegramBackButtonController() {
	const navigate = useNavigate()
	const location = useLocation()
	const navigationType = useNavigationType()

	/*
	 * React Router gives every history entry a `location.key`.
	 * We maintain our own stack of keys so we know exactly how deep
	 * the user is inside the Mini App — `window.history.length` can't
	 * tell us whether previous entries belong to us or to Telegram's
	 * webview.
	 */
	const keysRef = useRef<string[]>([location.key])
	const indexRef = useRef(0)

	// Register the Telegram click listener once.
	useEffect(() => {
		if (!isTelegram) return

		const handleBack = () => {
			if (indexRef.current > 0) {
				navigate(-1)
				return
			}

			if (CLOSE_AT_ROOT) {
				miniApp.close()
			}
			// When CLOSE_AT_ROOT is false, do nothing — button should already
			// be hidden at root, but guard against stale clicks.
		}

		backButton.onClick(handleBack)

		return () => {
			backButton.offClick(handleBack)
			backButton.hide()
		}
	}, [navigate])

	// Synchronize button visibility with React Router history.
	useEffect(() => {
		if (!isTelegram) return

		const currentKey = location.key
		const keys = keysRef.current

		switch (navigationType) {
			case 'PUSH': {
				// Remove any forward history and append the new page.
				keysRef.current = [
					...keys.slice(0, indexRef.current + 1),
					currentKey,
				]
				indexRef.current += 1
				break
			}

			case 'REPLACE': {
				// Replace the current entry without changing history depth.
				keysRef.current[indexRef.current] = currentKey
				break
			}

			case 'POP': {
				// User moved backward or forward.
				const existingIndex = keys.indexOf(currentKey)

				if (existingIndex >= 0) {
					indexRef.current = existingIndex
				} else {
					// Opened directly at this URL, refreshed, or restored
					// without known in-session history.
					keysRef.current = [currentKey]
					indexRef.current = 0
				}

				break
			}
		}

		const canGoBack = indexRef.current > 0

		if (canGoBack || CLOSE_AT_ROOT) {
			backButton.show()
		} else {
			backButton.hide()
		}
	}, [location.key, navigationType])

	return null
}
